/**
 * Canvas renderer for chapter completion seals + share-URL helpers.
 *
 * The seal is drawn on a Canvas so we can:
 *  - Download as a high-res PNG (rasterised for socials, unlike jsPDF's PDF)
 *  - Embed on the shareable /seal/:sectionId page
 *  - Copy the shareable URL for pasting anywhere
 */

const CREAM = "#F5F0E8";
const OBSIDIAN = "#17161A";
const TERRA = "#B6A27A";
const MUTED = "#75726E";
const SEAL_BG = "#E6E1D9";

// Rendered at 2x resolution for crisp social previews on retina displays.
const CANVAS_W = 1200;
const CANVAS_H = 900;

/**
 * Draw the seal on a canvas element and return it (HTMLCanvasElement).
 * Caller controls what to do with it: download, upload, embed.
 */
export function renderSealCanvas({ section, userName, completedAt }) {
  const canvas = document.createElement("canvas");
  canvas.width = CANVAS_W;
  canvas.height = CANVAS_H;
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;

  // Background
  ctx.fillStyle = CREAM;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

  // Outer border (terracotta) + inner (muted)
  ctx.strokeStyle = TERRA;
  ctx.lineWidth = 2;
  ctx.strokeRect(48, 48, CANVAS_W - 96, CANVAS_H - 96);
  ctx.strokeStyle = MUTED;
  ctx.lineWidth = 1;
  ctx.strokeRect(60, 60, CANVAS_W - 120, CANVAS_H - 120);

  // Top eyebrow — "UMBRA · A SHADOW JOURNAL"
  ctx.fillStyle = TERRA;
  ctx.font = "italic 20px Georgia, 'Times New Roman', serif";
  ctx.textAlign = "center";
  ctx.fillText("UMBRA  ·  A SHADOW JOURNAL", CANVAS_W / 2, 130);
  // Short line under
  ctx.beginPath();
  ctx.moveTo(CANVAS_W / 2 - 60, 145);
  ctx.lineTo(CANVAS_W / 2 + 60, 145);
  ctx.strokeStyle = TERRA;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Intro
  ctx.fillStyle = MUTED;
  ctx.font = "italic 22px Georgia, serif";
  ctx.fillText("This is to mark the quiet completion of", CANVAS_W / 2, 210);

  // Chapter subtitle (small caps, terra)
  ctx.fillStyle = TERRA;
  ctx.font = "18px Georgia, serif";
  ctx.fillText(section.subtitle.toUpperCase(), CANVAS_W / 2, 255);

  // Chapter title — hero
  ctx.fillStyle = OBSIDIAN;
  ctx.font = "70px Georgia, serif";
  ctx.fillText(section.title, CANVAS_W / 2, 340);

  // Chapter description
  ctx.fillStyle = MUTED;
  ctx.font = "italic 20px Georgia, serif";
  wrapText(ctx, section.description, CANVAS_W / 2, 395, CANVAS_W - 260, 30);

  // Seal (right side)
  const sealX = CANVAS_W - 230;
  const sealY = CANVAS_H - 300;
  const sealR = 110;
  ctx.fillStyle = SEAL_BG;
  ctx.beginPath();
  ctx.arc(sealX, sealY, sealR, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = TERRA;
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(sealX, sealY, sealR - 10, 0, Math.PI * 2);
  ctx.stroke();
  ctx.fillStyle = TERRA;
  ctx.font = "italic 18px Georgia, serif";
  ctx.fillText("UMBRA", sealX, sealY - 30);
  ctx.fillStyle = OBSIDIAN;
  ctx.font = "20px Georgia, serif";
  ctx.fillText("SEAL OF", sealX, sealY - 4);
  ctx.fillText("QUIET", sealX, sealY + 24);
  ctx.fillText("COMPLETION", sealX, sealY + 52);

  // Name block (left side)
  const nameX = 130;
  const nameY = CANVAS_H - 280;
  ctx.textAlign = "left";
  ctx.fillStyle = MUTED;
  ctx.font = "italic 20px Georgia, serif";
  ctx.fillText("Completed by", nameX, nameY);

  ctx.fillStyle = OBSIDIAN;
  ctx.font = "italic 56px Georgia, serif";
  const cleanName = ((userName || "A quiet journaler") + "").trim().slice(0, 40);
  ctx.fillText(cleanName, nameX, nameY + 70);

  // Underline
  ctx.strokeStyle = OBSIDIAN;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(nameX, nameY + 85);
  ctx.lineTo(nameX + Math.min(700, ctx.measureText(cleanName).width + 40), nameY + 85);
  ctx.stroke();

  // Date + prompt count
  const stamp = new Date(completedAt || Date.now()).toLocaleDateString(undefined, {
    year: "numeric", month: "long", day: "numeric",
  });
  const promptCount = section.prompts?.length || 4;
  ctx.fillStyle = MUTED;
  ctx.font = "18px Georgia, serif";
  ctx.fillText(`${promptCount} prompts spoken · Kept quietly on ${stamp}`, nameX, nameY + 125);

  // Closing whisper
  ctx.textAlign = "center";
  ctx.fillStyle = MUTED;
  ctx.font = "italic 18px Georgia, serif";
  ctx.fillText("You did not disappear. You listened.", CANVAS_W / 2, CANVAS_H - 90);

  return canvas;
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = String(text || "").split(/\s+/);
  let line = "";
  let cy = y;
  for (let i = 0; i < words.length; i++) {
    const testLine = line + words[i] + " ";
    const w = ctx.measureText(testLine).width;
    if (w > maxWidth && i > 0) {
      ctx.fillText(line.trim(), x, cy);
      line = words[i] + " ";
      cy += lineHeight;
    } else {
      line = testLine;
    }
  }
  ctx.fillText(line.trim(), x, cy);
}

/**
 * Trigger a browser download of the seal as a PNG.
 */
export function downloadSealPng({ section, userName, completedAt }) {
  const canvas = renderSealCanvas({ section, userName, completedAt });
  const dataUrl = canvas.toDataURL("image/png");
  const safeTitle = section.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = `umbra-seal-${safeTitle}.png`;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Build a shareable URL for the /seal/:sectionId page.
 * `name` and `completed` are query params so the recipient's page renders the same seal.
 */
export function getSealShareUrl({ section, userName, completedAt, origin }) {
  const params = new URLSearchParams();
  if (userName) params.set("name", userName);
  if (completedAt) params.set("completed", completedAt);
  const base = (origin || (typeof window !== "undefined" ? window.location.origin : ""));
  const qs = params.toString();
  return `${base}/seal/${section.id}${qs ? "?" + qs : ""}`;
}
